import { useMemo } from 'react';

interface ProgressBarProps {
  progress?: number;
  showAnimation?: boolean;
  currentStep?: number;
  totalSteps?: number;
}

export default function ProgressBar({
  progress = 0,
  showAnimation = false,
  currentStep,
  totalSteps = 5,
}: ProgressBarProps) {
  const clampedProgress = useMemo(() => {
    if (currentStep !== undefined) {
      return Math.min(Math.max((currentStep / totalSteps) * 100, 0), 100);
    }
    return Math.min(Math.max(progress, 0), 100);
  }, [progress, currentStep, totalSteps]);

  // Use explicit step text if currentStep is provided
  const getStepText = () => {
    if (currentStep !== undefined) {
      return `Step ${currentStep} of ${totalSteps}`;
    }
    if (progress <= 0) return 'Step 1 of 4';
    if (progress <= 25) return 'Step 2 of 4';
    if (progress <= 50) return 'Step 3 of 4';
    return 'Step 4 of 4';
  };

  return (
    <div className="w-full flex items-center gap-4 mb-4">
      <div className="flex-1 bg-border-dark rounded-full overflow-hidden h-[6px] relative">
        <div
          className={`h-full bg-primary rounded-full transition-transform duration-500 ease-out
            ${showAnimation ? 'bg-[length:1rem_1rem] bg-[linear-gradient(45deg,var(--color-primary)_25%,transparent_25%,transparent_50%,var(--color-primary)_50%,var(--color-primary)_75%,transparent_75%,transparent)] animate-[moveStripes_1s_linear_infinite]' : ''}`}
          style={{
            width: '100%',
            transform: `scaleX(${clampedProgress / 100})`,
            transformOrigin: 'left',
          }}
        ></div>
      </div>
      <span className="text-body text-gray-500 font-medium whitespace-nowrap">{getStepText()}</span>
    </div>
  );
}
